import React, {useEffect, useRef} from "react";

import {normalizeGdnAccentColor} from "../../libs/gdnColorCycle";
import {ColorPickerPanel} from "../../layout/ColorPicker";
import "./gdn-accent-control.css";

type GdnAccentControlProps = {
  id: string;
  color: string;
  hasOverride: boolean;
  onChange: (color: string | null) => void;
};

const GdnAccentControl = ({
  id,
  color,
  hasOverride,
  onChange,
}: GdnAccentControlProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = `gdn-accent-popover-${id}`;

  useEffect(() => {
    const popover = popoverRef.current;
    if (!popover) return;

    const positionPopover = (event: Event) => {
      if ((event as ToggleEvent).newState !== "open") return;
      const button = buttonRef.current;
      if (!button) return;
      const buttonRect = button.getBoundingClientRect();
      const width = popover.offsetWidth || 240;
      const left = Math.max(8, Math.min(buttonRect.left, window.innerWidth - width - 8));
      popover.style.top = `${buttonRect.bottom + 8}px`;
      popover.style.left = `${left}px`;
    };

    popover.addEventListener("beforetoggle", positionPopover);
    return () => popover.removeEventListener("beforetoggle", positionPopover);
  }, []);

  const commitColor = (value: string) => onChange(normalizeGdnAccentColor(value));

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`round-btn gdn-accent-control__trigger${hasOverride ? " active" : ""}`}
        title="Edit GDN accent color"
        aria-label="Edit GDN accent color"
        popoverTarget={popoverId}
      >
        <span className="gdn-accent-control__swatch" style={{backgroundColor: color}} />
      </button>
      <div
        ref={popoverRef}
        id={popoverId}
        popover="auto"
        className="gdn-accent-popover"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <span className="gdn-accent-popover__label">GDN accent color</span>
        <ColorPickerPanel value={color} onChange={commitColor} />
        <button
          type="button"
          className="gdn-accent-popover__reset"
          disabled={!hasOverride}
          onClick={() => onChange(null)}
        >
          Use product color
        </button>
      </div>
    </>
  );
};

export default GdnAccentControl;
